/**
 * clickable-vehicle-state.js — THE single owner of vehicle state.
 * Contract: docs/BUILD-REFERENCE-AUGUST.md §2. No other file may write these keys.
 *
 * COMPLETENESS CONTRACT (FITMENT-HARDENING-01, Lucas ruling 05 Aug —
 * supreme over any prior behaviour): fit verification exists ONLY at
 * full-model state (make + family + chassis + model). Make/family/chassis
 * contexts are BROWSE SCOPE — they may filter listings, decorate links and
 * prune menus, but NEVER produce a fit tick, matched box, amber-check, red
 * block, or a verified cart line.
 *   - activeVehicleName/activeVehicleTag hold VERIFIED (full-model) state
 *     ONLY. Nothing below full model is ever written to these keys, so
 *     every reader — including parse-time direct localStorage reads — is
 *     safe by construction.
 *   - browseContextName/browseContextTag hold partial context (today: the
 *     three bare makes, the dictionary's only partial entries).
 *   - fitVerdict(tags) is THE choke point: the only fit-verdict producer.
 *     It asserts VERIFIED at entry and returns the no-vehicle result
 *     otherwise, drift-logging the attempt.
 *   - The full-model shape (FULL_MODEL_RE) is in LOCKSTEP with veh_re in
 *     scripts/generate-storefront-data.py and the copy in
 *     snippets/clickable-vehicle-guard.liquid (the sync head guard that
 *     also evicts pre-hardening stored partials at parse time).
 *
 * Keys: activeVehicleName, activeVehicleTag, browseContextName,
 * browseContextTag, saveSearch, allTags, filters.
 * Writes store RAW strings (never JSON-stringified scalars); saveSearch and
 * allTags are arrays and are stored as JSON. Reads tolerate legacy quoted
 * values left behind by the old theme.
 *
 * Every state change dispatches CustomEvent 'clickable:vehicle-changed' with
 * detail {name, tag, contextName, contextTag, level} (r4b). name/tag are the
 * VERIFIED vehicle only (null when none — subscribers painting fit surfaces
 * are safe by construction); contextName/contextTag are the browse-scope
 * fallback (equal to name/tag when verified); level is 'VERIFIED',
 * 'BROWSE_CONTEXT' or null.
 */
(function () {
  'use strict';

  // LOCKSTEP: generate-storefront-data.py veh_re + clickable-vehicle-guard.
  var FULL_MODEL_RE = /^(BMW|Volkswagen|Audi)\s+(.+?)\s+\(([^)]*)\)\s+(.+)$/;
  var MAKES = ['BMW', 'Volkswagen', 'Audi'];

  function levelOf(name) {
    if (window.clickableVehicleLevel) return window.clickableVehicleLevel(name);
    if (!name) return null;
    return FULL_MODEL_RE.test(name) ? 'VERIFIED' : 'BROWSE_CONTEXT';
  }

  migrateLegacyStorage();

  // One-time, version-gated cleanup of old-theme storage. INVARIANT: a
  // pathological stored value must never take the page down — everything is
  // inside the try/catch, and the version flag is only set on success so a
  // failed migration retries on the next load.
  function migrateLegacyStorage() {
    try {
      if (localStorage.getItem('clickableStorageV') === '1') return;

      var dictionary = Array.isArray(window.clickableAllTags) ? window.clickableAllTags : [];
      // Dictionary not yet available (first visit: the allTags asset is
      // still fetching) -- do NOT validate stored vehicles against an empty
      // list (it would wipe them). Version flag stays unset so migration
      // retries next load, when the fetched cache is present at parse time.
      if (!dictionary.length) return;

      var name = localStorage.getItem('activeVehicleName');
      var tag = localStorage.getItem('activeVehicleTag');
      if (name !== null) name = name.replace(/"/g, '');
      if (tag !== null) tag = tag.replace(/"/g, '');
      // Completeness contract: the dictionary CONTAINS the three bare makes
      // (URL-hydration vocabulary), so membership alone is not enough — a
      // legacy-stored partial must land in browse context, never the
      // verified keys. (The sync head guard normally evicts these before
      // this runs; this is the belt for a guard-less load.)
      if (name !== null && dictionary.indexOf(name) !== -1 && levelOf(name) === 'VERIFIED') {
        localStorage.setItem('activeVehicleName', name);
        if (tag !== null) localStorage.setItem('activeVehicleTag', tag);
      } else {
        localStorage.removeItem('activeVehicleName');
        localStorage.removeItem('activeVehicleTag');
        if (name !== null && MAKES.indexOf(name) !== -1) {
          localStorage.setItem('browseContextName', name);
          localStorage.setItem('browseContextTag', toTag(name));
        }
      }

      // Known-legacy keys. allTags in its old CDN-fetched form goes too —
      // clickable-all-tags.liquid rewrites it (and already ran this load,
      // so re-persist the new form rather than leaving a one-load gap).
      ['allTags', 'topProducts', 'selectedSortValue', 'no-mobile-qf', 'hideLoginPrompt', 'filters']
        .forEach(function (key) {
          localStorage.removeItem(key);
        });
      if (dictionary.length) {
        localStorage.setItem('allTags', JSON.stringify(dictionary));
      }

      localStorage.setItem('clickableStorageV', '1');
    } catch (error) {
      console.log('clickable-vehicle-state: storage migration skipped', error);
    }
  }

  // Legacy tolerance: old EasySearch writes were JSON.stringify'd (quoted).
  function read(key) {
    var value = localStorage.getItem(key);
    return value === null ? null : value.replace(/"/g, '');
  }

  function toTag(name) {
    return name.replace(/[ ./]/g, '-');
  }

  // Verified accessors: return the full-model vehicle ONLY. A stored value
  // that fails the completeness test is treated as absent (read-side belt —
  // the write side already never stores partials in these keys).
  function verifiedName() {
    var name = read('activeVehicleName');
    return levelOf(name) === 'VERIFIED' ? name : null;
  }

  function verifiedTag() {
    return verifiedName() === null ? null : read('activeVehicleTag');
  }

  function browseName() {
    return read('browseContextName');
  }

  function browseTag() {
    return read('browseContextTag');
  }

  function dispatchChange() {
    var name = verifiedName();
    var tag = verifiedTag();
    var ctxName = name !== null ? name : browseName();
    var ctxTag = name !== null ? tag : browseTag();
    document.dispatchEvent(
      new CustomEvent('clickable:vehicle-changed', {
        detail: {
          name: name,
          tag: tag,
          contextName: ctxName,
          contextTag: ctxTag,
          level: name !== null ? 'VERIFIED' : (ctxName !== null ? 'BROWSE_CONTEXT' : null),
        },
      })
    );
  }

  // Drift logger (FITMENT-HARDENING-01 §2): every partial write and every
  // fit evaluation attempted against partial state is recorded — these are
  // the leak paths. First occurrence per page logs; the counter keeps
  // counting (probe surface: clickableVehicleState.driftCount()).
  var driftSeen = {};
  var driftTotal = 0;

  function driftLog(message) {
    driftTotal++;
    if (driftSeen[message]) return;
    driftSeen[message] = true;
    console.info('[fitment-drift] ' + message);
  }

  // tag is optional; derived from name when omitted. URL hydration passes the
  // URL's own segment so the stored tag mirrors the address bar (URL wins).
  // THE WRITE CHOKE: every write is classified here. Anything below full
  // model can only ever land in browse context — no caller can put a
  // partial in the verified keys because there is nothing else to call.
  function setVehicle(name, tag) {
    if (!name) return;
    if (levelOf(name) !== 'VERIFIED') {
      setBrowseContext(name, tag);
      return;
    }
    var finalTag = tag || toTag(name);
    if (read('activeVehicleName') === name && read('activeVehicleTag') === finalTag) return;
    localStorage.setItem('activeVehicleName', name);
    localStorage.setItem('activeVehicleTag', finalTag);
    // Verified state absorbs any browse context (it can only have led here).
    localStorage.removeItem('browseContextName');
    localStorage.removeItem('browseContextTag');
    localStorage.removeItem('filters');
    dispatchChange();
  }

  // Partial context (make today; family/chassis if they ever join the
  // dictionary). Browse scope only: filters listings, decorates links.
  // NEVER downgrades or overwrites a verified vehicle (Lucas ruling leg:
  // full vehicle + make-level URL = vehicle survives, context write is a
  // no-op). Vocabulary-gated: only names the dictionary (or the make list)
  // knows can become context — arbitrary partial strings (e.g. a
  // half-filled cascade join) are dropped outright.
  function setBrowseContext(name, tag) {
    if (!name) return;
    if (verifiedName() !== null) {
      driftLog('browse-context write ignored — verified vehicle retained ("' + verifiedName() + '")');
      return;
    }
    var known = getAllTags().indexOf(name) !== -1 || MAKES.indexOf(name) !== -1;
    if (!known) {
      driftLog('partial vehicle write dropped — not in the context vocabulary ("' + name + '")');
      return;
    }
    var finalTag = tag || toTag(name);
    if (read('browseContextName') === name && read('browseContextTag') === finalTag) return;
    // Logged only when context actually CHANGES (r4b refinement, Delta 33:
    // an idempotent re-hydration of standing context is normal browsing —
    // the logger stays SILENT on steady-state partial pages).
    driftLog('partial vehicle write established BROWSE_CONTEXT ("' + name + '")');
    localStorage.setItem('browseContextName', name);
    localStorage.setItem('browseContextTag', finalTag);
    dispatchChange();
  }

  function clearVehicle() {
    localStorage.removeItem('activeVehicleName');
    localStorage.removeItem('activeVehicleTag');
    localStorage.removeItem('browseContextName');
    localStorage.removeItem('browseContextTag');
    localStorage.removeItem('filters');
    dispatchChange();
  }

  // THE FIT CHOKE POINT (FITMENT-HARDENING-01 §2): the ONLY producer of a
  // fit verdict. Asserts VERIFIED at entry; below full model it returns the
  // no-vehicle result and drift-logs the attempted evaluation. Surfaces
  // paint from this — they never derive a verdict from raw state.
  // Returns {verdict: 'unset'|'fit'|'red', name, tag}; name/tag are the
  // verified vehicle (null on 'unset'). Generic-product bypass stays
  // surface-side (a generic product never consults vehicle state).
  function fitVerdict(tags) {
    var name = verifiedName();
    if (name === null) {
      // r4b refinement (Delta 33): an evaluation under browse context is the
      // NORMAL paint path through the choke — it cannot consume partial
      // state by construction, so it is not drift and is not logged (the
      // acceptance criterion is a SILENT logger on steady-state partial
      // pages). Drift = the write-side logs: junk-vocabulary drops,
      // downgrade-attempt ignores, first context establishment.
      return { verdict: 'unset', name: null, tag: null };
    }
    /* LOCKSTEP DELEGATION (Lucas ruling, 21 Aug — exact-reservation
       build): the comparison lives ONLY in clickable-fit-predicate.js
       (sync head, fourth lockstep member). fitVerdict KEEPS the Tier-0
       choke-point role; predicate absent = fail closed to 'unset'
       (silent baseline), never 'red' (which would paint mismatch on
       every card). */
    if (!window.clickableFitPredicate) return { verdict: 'unset', name: null, tag: null };
    var fits = window.clickableFitPredicate.hasVehicleTag(tags, name);
    return { verdict: fits ? 'fit' : 'red', name: name, tag: verifiedTag() };
  }

  function getAllTags() {
    if (Array.isArray(window.clickableAllTags)) return window.clickableAllTags;
    try {
      return JSON.parse(localStorage.getItem('allTags')) || [];
    } catch (error) {
      return [];
    }
  }

  // EasySearch capture (Build Reference §3): the cascade UI is injected at
  // runtime by the app's script, so listen via delegation. Field IDs are
  // shop-config-stable hardcodes carried from the old theme.
  //
  // VEHICLE ENTRY CONTEXT RULE (Lucas, 11 Aug): on a PRODUCT PAGE the
  // QuickFinder submission resolves IN PLACE -- a WINDOW-CAPTURE listener
  // runs before the app's own handler, performs the state write itself and
  // kills the event so the app never navigates. Everywhere else the app's
  // redirect to the filtered store stands (the bubble listener below only
  // mirrors state).
  window.addEventListener('click', function (event) {
    if (!event.target || event.target.id !== 'easysearch-search') return;
    if (!document.querySelector('[data-pdp-root]')) return;
    var captured = captureEasySearchFields();
    if (!captured) return; // incomplete cascade: let the app handle/validate
    event.preventDefault();
    event.stopImmediatePropagation();
    if (window.clickableFitmentReveal) window.clickableFitmentReveal();
  }, true);

  function captureEasySearchFields() {
    var fieldIds = {
      make: 'easysearch_field_14917',
      series: 'easysearch_field_14918',
      chassis: 'easysearch_field_14919',
      model: 'easysearch_field_15247',
    };
    var makeSelect = document.getElementById(fieldIds.make);
    var seriesSelect = document.getElementById(fieldIds.series);
    var chassisSelect = document.getElementById(fieldIds.chassis);
    var modelSelect = document.getElementById(fieldIds.model);
    if (!makeSelect || !seriesSelect || !chassisSelect || !modelSelect) return false;
    if (!makeSelect.value || !seriesSelect.value || !chassisSelect.value || !modelSelect.value) return false;

    var parts = [makeSelect.value, seriesSelect.value, chassisSelect.value, modelSelect.value];
    var name = parts.join(' ');
    localStorage.setItem('saveSearch', JSON.stringify(parts));
    setVehicle(name);
    return true;
  }

  document.addEventListener('click', function (event) {
    if (!event.target || event.target.id !== 'easysearch-search') return;

    var fieldIds = {
      make: 'easysearch_field_14917',
      series: 'easysearch_field_14918',
      chassis: 'easysearch_field_14919',
      model: 'easysearch_field_15247',
    };
    var makeSelect = document.getElementById(fieldIds.make);
    var seriesSelect = document.getElementById(fieldIds.series);
    var chassisSelect = document.getElementById(fieldIds.chassis);
    var modelSelect = document.getElementById(fieldIds.model);
    if (!makeSelect || !seriesSelect || !chassisSelect || !modelSelect) return;

    var parts = [makeSelect.value, seriesSelect.value, chassisSelect.value, modelSelect.value];
    var name = parts.join(' ');

    localStorage.setItem('saveSearch', JSON.stringify(parts));
    // Scoped navigation follows here (the app redirects to the filtered
    // store; this bubble path runs on non-PDP surfaces only -- the capture
    // listener above stopImmediatePropagation()s on a PDP). Raise the
    // re-vehicling dim BEFORE the state write so the grid never flashes
    // wrong-vehicle warnings; begin() no-ops where no grid renders.
    if (window.clickableRevehicle) window.clickableRevehicle.begin();
    setVehicle(name);
  });

  // ---- Vehicle URL resolver (Task 04c §C) — three tiers.
  // Tier 0 finding (24 Jul, dev preview): Shopify native tag filtering is
  // CASE-INSENSITIVE — a wrong-case segment renders the identical grid. The
  // canonical redirect is URL hygiene (SEO/dedup), not functionality.
  // Always store the CANONICAL tag derived from the resolved dictionary name,
  // never the raw URL segment (fixes the old theme's mangled-tag propagation).
  function normalizeForLookup(value) {
    return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  // Tier 3 mask: drop every (...) group — the build range is the part that
  // drifts (147 dictionary vehicles carry open '202x' ranges that WILL be
  // renamed to concrete years). '202x' therefore matches any concrete year
  // by virtue of the whole range being masked. Only a UNIQUE masked match
  // resolves; ambiguity never guesses.
  function maskRanges(value) {
    return normalizeForLookup(value.replace(/\([^)]*\)/g, ' '));
  }

  // Resolve ONE +-split part through the three tiers; null if no vehicle.
  function resolvePart(rawPart) {
    var allTags = getAllTags();
    var i;

    // Tier 1: exact canonical segment
    for (i = 0; i < allTags.length; i++) {
      if (toTag(allTags[i]) === rawPart) return allTags[i];
    }

    // Tier 2: case/mangle-tolerant dictionary match
    var target = normalizeForLookup(rawPart);
    for (i = 0; i < allTags.length; i++) {
      if (normalizeForLookup(allTags[i]) === target) return allTags[i];
    }

    // Tier 3: range-tolerant — unique match with (...) portions masked
    var masked = maskRanges(rawPart);
    if (masked) {
      var candidates = allTags.filter(function (name) {
        return maskRanges(name) === masked;
      });
      if (candidates.length === 1) return candidates[0];
    }
    return null;
  }

  function hydrateFromUrl() {
    var segments = window.location.pathname.split('/').filter(Boolean);
    if (segments[0] !== 'collections' || segments.length < 3) return;

    // Multi-tag segments (Task 07b browse-by): '{tag1}+{tag2}' is native
    // Shopify AND-filtering. Split on '+', resolve the VEHICLE part through
    // the tiers; non-vehicle parts (taxonomy tags) pass through untouched.
    var rawSegment = decodeURIComponent(segments[segments.length - 1]);
    var parts = rawSegment.split('+');
    var resolvedName = null;
    var vehicleIndex = -1;

    for (var p = 0; p < parts.length; p++) {
      var name = resolvePart(parts[p]);
      if (name) {
        resolvedName = name;
        vehicleIndex = p;
        break;
      }
    }

    if (!resolvedName) return;

    // setVehicle classifies: a full-model segment verifies; a make segment
    // (the dictionary's bare-brand entries) lands in BROWSE CONTEXT only —
    // and never touches an existing verified vehicle. Idempotent writes
    // no-op inside the module.
    var canonicalTag = toTag(resolvedName);
    setVehicle(resolvedName, canonicalTag);
    if (parts[vehicleIndex] !== canonicalTag) {
      // Canonicalise ONLY the vehicle part; taxonomy parts keep their form.
      parts[vehicleIndex] = canonicalTag;
      var path = window.location.pathname;
      var canonicalPath = path.slice(0, path.lastIndexOf('/') + 1) +
        parts.map(function (part) {
          return encodeURIComponent(part).replace(/%2F/gi, '/');
        }).join('+');
      window.location.replace(canonicalPath + window.location.search + window.location.hash);
    }
  }

  // ---- Outbound vehicle-context navigation (Build Reference §2 read-points).
  // ONE decorator for every surface: appends /{activeVehicleTag} to collection
  // links when a vehicle is set, strips on clear. Templates opt OUT, not in:
  //   - never inside [data-suppress-fitment] (brand/chooser surfaces)
  //   - never inside #ProductGridContainer (facet/pagination links carry state)
  //   - never brand collections, already-tagged URLs, external links
  var BRAND_HANDLES = ['audi', 'bmw', 'volkswagen'];

  function decoratable(link) {
    var href = link.getAttribute('href');
    if (!href || href.indexOf('/collections/') !== 0) return false;
    // [data-red-cta]: the PDP mismatch CTA owns its own vehicle-tag append
    // (fitment.js) — double decoration would stack two tags into one URL.
    // [data-browse-nav]: the browse menu's decorateBrowse is the single href
    // owner (Option A collection links are bare 2-segment and would otherwise
    // fall in scope here — store-form links never did).
    if (link.closest('[data-suppress-fitment], #ProductGridContainer, .facets-wrapper, [data-red-cta], [data-browse-nav]')) return false;
    var path = href.split('?')[0].split('#')[0];
    var segments = path.split('/').filter(Boolean);
    if (segments.length !== 2) return false; // only /collections/{handle} — never re-tag tagged or deeper URLs
    if (BRAND_HANDLES.indexOf(segments[1].toLowerCase()) !== -1) return false;
    return true;
  }

  function decorateLinks(tag) {
    document.querySelectorAll('a[href^="/collections/"]').forEach(function (link) {
      var original = link.getAttribute('data-vehicle-href-base');
      if (original !== null) {
        // Previously decorated: restore, then re-decorate or leave stripped.
        link.setAttribute('href', original);
        link.removeAttribute('data-vehicle-href-base');
      }
      if (!tag || !decoratable(link)) return;
      var href = link.getAttribute('href');
      var split = href.split(/(?=[?#])/);
      link.setAttribute('data-vehicle-href-base', href);
      link.setAttribute('href', split[0] + '/' + tag + (split[1] || ''));
    });
  }

  window.clickableVehicleState = {
    toTag: toTag,
    // Verified (full-model) vehicle ONLY — fit surfaces and anything
    // vehicle-set-styled read these. Partial context never appears here.
    getName: verifiedName,
    getTag: verifiedTag,
    // Browse scope (verified falls through first): link decoration and
    // listing filters — the powers partial context keeps.
    getContextName: function () { var n = verifiedName(); return n !== null ? n : browseName(); },
    getContextTag: function () { var n = verifiedName(); return n !== null ? verifiedTag() : browseTag(); },
    getLevel: function () {
      if (verifiedName() !== null) return 'VERIFIED';
      return browseName() !== null ? 'BROWSE_CONTEXT' : null;
    },
    fitVerdict: fitVerdict,
    driftCount: function () { return driftTotal; },
    setVehicle: setVehicle,
    clearVehicle: clearVehicle,
    decorateLinks: decorateLinks,
    resolveUrl: hydrateFromUrl,
    // Read-only tag resolver (clickable-revehicle.js bfcache leg): the
    // vehicle NAME a raw URL segment scopes to, or null. Never writes state.
    resolveTag: resolvePart,
    // Re-emit the CURRENT state's 'clickable:vehicle-changed' (clickable-
    // revehicle.js bfcache re-sync): every subscriber's repaint is
    // idempotent, so a stale bfcache paint reconciles to current state.
    revalidate: dispatchChange,
  };

  document.addEventListener('clickable:vehicle-changed', function (event) {
    // Decoration follows browse scope: a make context keeps decorating
    // collection links (filtering power), while detail.name stays
    // verified-only for every fit surface.
    decorateLinks(event.detail.contextTag);
  });
  document.addEventListener('DOMContentLoaded', function () {
    decorateLinks(window.clickableVehicleState.getContextTag());
  });

  hydrateFromUrl();
})();
